import { Image as ExpoImage } from 'expo-image';
import { VideoView, useVideoPlayer } from 'expo-video';
import { History, Pencil, Trash2 } from 'lucide-react-native';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  type LayoutChangeEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { IdentityHeader } from '@/src/components/IdentityHeader';
import { LinkPreviewCard } from '@/src/features/posts/LinkPreviewCard';
import {
  PhotoAttachment,
  type PhotoContext,
} from '@/src/features/posts/PhotoAttachment';
import { ProfileMentionText } from '@/src/features/posts/ProfileMentionText';
import {
  CompactReactionButton,
  ReactionSummaryButton,
  type ReactionTarget,
} from '@/src/features/posts/ReactionControls';
import {
  getMediaAspectRatio,
  type CommentMedia,
  type LinkPreview,
  type ProfileMention,
  type Reaction,
  type ReactionCounts,
} from '@/src/features/posts/post-service';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';

import { CommentRowFrame } from './CommentRowFrame';
import {
  buildStableThreadedComments,
  replyTargetForComment,
  type CommentSortMode,
  type CommentReplyTarget,
  type StableThreadedCommentOrderState,
  type ThreadableComment,
} from './comment-threading';

type CommentSyncStatus = 'pending' | 'uploading' | 'synced' | 'failed';

export type CommentThreadRowComment = ThreadableComment & {
  aiGenerated?: boolean;
  authorFounderBadge?: boolean;
  authorPhotoURL: string | null;
  authorVerifiedBadge?: boolean;
  body: string | null;
  botGenerated?: boolean;
  editCount?: number | null;
  linkPreview: LinkPreview | null;
  media: CommentMedia | null;
  profileMentions: ProfileMention[];
  syncStatus?: CommentSyncStatus;
};

export type CommentMediaLayout = 'aspect' | 'square';

type CommentThreadRowLabels = {
  gifAccessibilityLabel: string;
  imageAccessibilityLabel: string;
  linkPreviewSource: string;
  mediaAnalyticsContext: PhotoContext;
  mentionSource: string;
  photoViewerTitle: string;
};

const DEFAULT_LABELS: CommentThreadRowLabels = {
  gifAccessibilityLabel: 'Comment GIF',
  imageAccessibilityLabel: 'Comment image',
  linkPreviewSource: 'comment_link_preview',
  mediaAnalyticsContext: 'comment',
  mentionSource: 'comment_mention',
  photoViewerTitle: 'comment photo',
};

function UploadedCommentVideo({
  aspectRatio,
  layout,
  uri,
}: {
  aspectRatio: number;
  layout: CommentMediaLayout;
  uri: string;
}) {
  const player = useVideoPlayer(uri);

  return (
    <VideoView
      nativeControls
      contentFit="contain"
      player={player}
      style={[
        styles.media,
        layout === 'aspect' ? styles.aspectMedia : null,
        { aspectRatio },
      ]}
    />
  );
}

function CommentThreadMedia({
  labels,
  layout,
  media,
}: {
  labels: CommentThreadRowLabels;
  layout: CommentMediaLayout;
  media: CommentMedia;
}) {
  const aspectRatio = getMediaAspectRatio(media);
  const mediaStyle = [
    styles.media,
    layout === 'aspect' ? [styles.aspectMedia, { aspectRatio }] : null,
  ];

  if (media.kind === 'video') {
    return (
      <UploadedCommentVideo
        aspectRatio={aspectRatio}
        layout={layout}
        uri={media.downloadURL}
      />
    );
  }

  if (media.kind === 'image') {
    return (
      <PhotoAttachment
        accessibilityLabel={labels.imageAccessibilityLabel}
        analyticsContext={labels.mediaAnalyticsContext}
        contentFit="cover"
        frameStyle={mediaStyle}
        imageStyle={styles.mediaImage}
        originalURL={media.downloadURL}
        thumbnailURL={media.thumbnail?.downloadURL}
        viewerTitle={labels.photoViewerTitle}
      />
    );
  }

  if (media.kind === 'gif') {
    return (
      <View style={styles.mediaWrap}>
        <ExpoImage
          accessibilityLabel={labels.gifAccessibilityLabel}
          cachePolicy="memory-disk"
          contentFit="cover"
          source={{ uri: media.previewURL || media.originalURL }}
          style={mediaStyle}
        />
        <Text style={styles.giphyAttribution}>via GIPHY</Text>
      </View>
    );
  }

  return null;
}

export function CommentThreadRow({
  canDelete,
  canEdit,
  comment,
  currentUid,
  deleteDisabled,
  editDisabled,
  isReply = false,
  labels: labelOverrides,
  mediaLayout = 'square',
  onAuthorPress,
  onDelete,
  onEdit,
  onReply,
  onSelectReaction,
  onShowEditHistory,
  reactionCounts,
  reactionDisabled,
  reactionLoading,
  reactionTarget,
  selectedReaction,
  showEditHistory,
  showSyncStatus,
}: {
  canDelete: boolean;
  canEdit: boolean;
  comment: CommentThreadRowComment;
  currentUid?: string;
  deleteDisabled?: boolean;
  editDisabled?: boolean;
  isReply?: boolean;
  labels?: Partial<CommentThreadRowLabels>;
  mediaLayout?: CommentMediaLayout;
  onAuthorPress?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onReply?: (target: CommentReplyTarget) => void;
  onSelectReaction: (reaction: Reaction) => void;
  onShowEditHistory?: () => void;
  reactionCounts: ReactionCounts;
  reactionDisabled?: boolean;
  reactionLoading?: boolean;
  reactionTarget: ReactionTarget;
  selectedReaction: Reaction | null;
  showEditHistory: boolean;
  showSyncStatus?: boolean;
}) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const fullName = comment.authorFullName || 'Frienzzi user';
  const authorUsername = comment.authorUsername;
  const authorPhotoURL = comment.authorPhotoURL;
  const authorVerifiedBadge = comment.authorVerifiedBadge === true;
  const authorFounderBadge = comment.authorFounderBadge === true;
  const totalReactions = Object.values(reactionCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const canReply = Boolean(currentUid && onReply);

  function openAuthorProfile() {
    if (onAuthorPress) {
      onAuthorPress();
      return;
    }

    openUserProfile({ currentUid, targetUid: comment.authorUid });
  }

  const headerActions =
    canEdit || canDelete ? (
      <>
        {canEdit ? (
          <Pressable
            accessibilityLabel="Edit comment"
            accessibilityRole="button"
            disabled={editDisabled}
            onPress={onEdit}
            style={({ pressed }) => [
              styles.iconButton,
              pressed && !editDisabled ? styles.iconButtonPressed : null,
              editDisabled ? styles.iconButtonDisabled : null,
            ]}
          >
            <Pencil color={colors.secondary} size={16} strokeWidth={2.3} />
          </Pressable>
        ) : null}
        {canDelete ? (
          <Pressable
            accessibilityLabel="Delete comment"
            accessibilityRole="button"
            disabled={deleteDisabled}
            onPress={onDelete}
            style={({ pressed }) => [
              styles.iconButton,
              pressed && !deleteDisabled ? styles.iconButtonPressed : null,
              deleteDisabled ? styles.iconButtonDisabled : null,
            ]}
          >
            <Trash2 color={colors.danger} size={17} strokeWidth={2.3} />
          </Pressable>
        ) : null}
      </>
    ) : null;
  const body = comment.body ? (
    <ProfileMentionText
      body={comment.body}
      currentUid={currentUid}
      mentions={comment.profileMentions ?? []}
      source={labels.mentionSource}
      style={styles.text}
    />
  ) : null;
  const mediaSlot = comment.media ? (
    <CommentThreadMedia
      labels={labels}
      layout={mediaLayout}
      media={comment.media}
    />
  ) : null;
  const linkPreviewSlot =
    !comment.media && comment.linkPreview ? (
      <LinkPreviewCard
        compact
        preview={comment.linkPreview}
        source={labels.linkPreviewSource}
      />
    ) : null;
  const toolbar = (
    <View style={styles.actions}>
      <View style={styles.actionLeft}>
        {showEditHistory ? (
          <Pressable
            accessibilityLabel="View comment edit history"
            accessibilityRole="button"
            onPress={onShowEditHistory}
            style={({ pressed }) => [
              styles.editedButton,
              pressed ? styles.editedButtonPressed : null,
            ]}
          >
            <History color={colors.primary} size={13} strokeWidth={2.5} />
            <Text style={styles.editedText}>Edited</Text>
          </Pressable>
        ) : null}
        <CompactReactionButton
          disabled={reactionDisabled}
          loading={reactionLoading}
          selectedReaction={selectedReaction}
          onSelect={onSelectReaction}
        />
        {totalReactions > 0 ? (
          <Text style={[styles.age, styles.reactionCount]}>
            {totalReactions}
          </Text>
        ) : null}
        {canReply ? (
          <Pressable
            accessibilityLabel={`Reply to ${fullName}`}
            accessibilityRole="button"
            onPress={() => onReply?.(replyTargetForComment(comment))}
            style={({ pressed }) => [
              styles.replyButton,
              pressed ? styles.replyButtonPressed : null,
            ]}
          >
            <Text style={styles.replyButtonText}>Reply</Text>
          </Pressable>
        ) : null}
      </View>
      <View style={styles.reactionSummary}>
        <ReactionSummaryButton
          counts={reactionCounts}
          currentUid={currentUid}
          selectedReaction={selectedReaction}
          target={reactionTarget}
        />
      </View>
    </View>
  );
  const syncStatus =
    showSyncStatus && comment.syncStatus && comment.syncStatus !== 'synced' ? (
      <Text style={styles.sync}>
        {comment.syncStatus === 'failed' ? 'Sync failed' : 'Pending sync'}
      </Text>
    ) : null;
  const aiStatus =
    comment.aiGenerated || comment.botGenerated ? (
      <Text style={styles.aiStatus}>AI-generated reply</Text>
    ) : null;
  const status =
    syncStatus || aiStatus ? (
      <View style={styles.statusRow}>
        {aiStatus}
        {syncStatus}
      </View>
    ) : null;

  return (
    <CommentRowFrame
      headerActions={headerActions}
      identity={
        <IdentityHeader
          avatarSize={isReply ? 32 : 36}
          currentUid={currentUid}
          density="compact"
          founderBadge={authorFounderBadge}
          fullName={fullName}
          photoURL={authorPhotoURL}
          timestamp={comment.createdAt}
          timestampPrefix="Commented"
          uid={comment.authorUid}
          username={authorUsername}
          verifiedBadge={authorVerifiedBadge}
          onPress={openAuthorProfile}
        />
      }
      footer={toolbar}
      isReply={isReply}
      status={status}
    >
      {body}
      {mediaSlot}
      {linkPreviewSlot}
    </CommentRowFrame>
  );
}

export function ThreadedCommentList<T extends ThreadableComment>({
  comments,
  groupStyle,
  onCommentLayout,
  renderComment,
  orderSessionKey,
  replyComposer,
  replyTarget,
  sortMode,
}: {
  comments: T[];
  groupStyle?: StyleProp<ViewStyle>;
  onCommentLayout?: (commentId: string, y: number) => void;
  orderSessionKey?: boolean | number | string | null;
  renderComment: (comment: T, isReply: boolean) => ReactNode;
  replyComposer?: ReactNode;
  replyTarget?: CommentReplyTarget | null;
  sortMode?: CommentSortMode;
}) {
  const orderStateRef = useRef<StableThreadedCommentOrderState | null>(null);
  const orderSessionKeyRef = useRef<typeof orderSessionKey>(orderSessionKey);
  const groupYByThreadIdRef = useRef<Record<string, number>>({});
  const relativeCommentLayoutsRef = useRef<
    Record<string, { threadId: string; y: number }>
  >({});
  const stableThreads = useMemo(() => {
    const previousOrderState =
      orderSessionKeyRef.current === orderSessionKey
        ? orderStateRef.current
        : null;

    return buildStableThreadedComments(comments, sortMode, previousOrderState);
  }, [comments, orderSessionKey, sortMode]);

  useEffect(() => {
    orderStateRef.current = stableThreads.orderState;
    orderSessionKeyRef.current = orderSessionKey;
  }, [orderSessionKey, stableThreads.orderState]);

  function notifyCommentLayout(commentId: string, threadId: string, y: number) {
    relativeCommentLayoutsRef.current[commentId] = { threadId, y };

    const groupY = groupYByThreadIdRef.current[threadId];

    if (typeof groupY === 'number') {
      onCommentLayout?.(commentId, groupY + y);
    }
  }

  function notifyGroupLayout(threadId: string, event: LayoutChangeEvent) {
    const groupY = event.nativeEvent.layout.y;

    groupYByThreadIdRef.current[threadId] = groupY;

    Object.entries(relativeCommentLayoutsRef.current).forEach(
      ([commentId, layout]) => {
        if (layout.threadId === threadId) {
          onCommentLayout?.(commentId, groupY + layout.y);
        }
      },
    );
  }

  return (
    <>
      {stableThreads.threads.map(({ comment, replies }) => (
        <View
          key={comment.id}
          onLayout={(event) => notifyGroupLayout(comment.id, event)}
          style={groupStyle}
        >
          <View
            onLayout={(event) =>
              notifyCommentLayout(
                comment.id,
                comment.id,
                event.nativeEvent.layout.y,
              )
            }
          >
            {renderComment(comment, false)}
          </View>
          {replyTarget?.replyToCommentId === comment.id ? replyComposer : null}
          {replies.map((reply) => (
            <View
              key={reply.id}
              onLayout={(event) =>
                notifyCommentLayout(
                  reply.id,
                  comment.id,
                  event.nativeEvent.layout.y,
                )
              }
            >
              {renderComment(reply, true)}
              {replyTarget?.replyToCommentId === reply.id
                ? replyComposer
                : null}
            </View>
          ))}
        </View>
      ))}
    </>
  );
}

const styles = StyleSheet.create({
  actionLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  aiStatus: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  actions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
    minHeight: 36,
  },
  age: {
    color: colors.mutedText,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: '800',
    minWidth: 28,
  },
  aspectMedia: {
    maxHeight: 260,
  },
  editedButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    gap: 3,
    minHeight: 28,
    paddingHorizontal: 2,
    paddingVertical: 2,
  },
  editedButtonPressed: {
    opacity: 0.78,
  },
  editedText: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  giphyAttribution: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  iconButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  iconButtonDisabled: {
    opacity: 0.45,
  },
  iconButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  media: {
    alignSelf: 'flex-start',
    aspectRatio: 1,
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    maxWidth: 320,
    overflow: 'hidden',
    width: '100%',
  },
  mediaImage: {
    borderRadius: 8,
    height: '100%',
    width: '100%',
  },
  mediaWrap: {
    gap: spacing.xs,
  },
  reactionCount: {
    marginLeft: -4,
  },
  reactionSummary: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    minHeight: 32,
  },
  replyButton: {
    borderRadius: 999,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  replyButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  replyButtonText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  sync: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '800',
  },
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  text: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
  username: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 14,
  },
});
